/**
 * Animation State Manager
 * Centralized management of animation states with priority system
 * Prevents race conditions and handles smooth transitions
 *
 * KEY FEATURES:
 * - Speaking gets EXCLUSIVE control of mouth morphs
 * - Priority-based interruption (lower number = higher priority)
 * - Protected animations cannot be interrupted except by force
 *
 * Nuamedia Solutions Pty Ltd
 */
import { AnimationPriority, AnimationType, getPriorityForType, canInterrupt } from '../utils/animationUtils';

// Mouth morphs that belong exclusively to speaking animation
export const MOUTH_MORPHS = new Set([
  'jawOpen', 'jawForward', 'jawLeft', 'jawRight', 'jaw_L', 'jaw_R',
  'mouthOpen', 'mouthClose', 'mouthFunnel', 'mouthPucker',
  'mouthUpperUp_L', 'mouthUpperUp_R', 'mouthLowerDown_L', 'mouthLowerDown_R',
  'mouthSmile_L', 'mouthSmile_R', 'mouthFrown_L', 'mouthFrown_R',
  'mouthStretch_L', 'mouthStretch_R', 'mouthDimple_L', 'mouthDimple_R',
  'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
  'mouthPress_L', 'mouthPress_R', 'mouthLeft', 'mouthRight',
  'tongueOut', 'cheekPuff', 'cheekSquint_L', 'cheekSquint_R'
]);

// Animation weights by priority (for blending)
export const ANIMATION_WEIGHTS: Record<AnimationType, number> = {
  [AnimationType.SPEAKING]: 1.0,   // Full weight - exclusive control
  [AnimationType.THINKING]: 0.8,   // Can blend with idle
  [AnimationType.LISTENING]: 0.6,  // Can blend with idle
  [AnimationType.IDLE]: 0.3        // Background animation
};

// Transition durations in milliseconds
export const FADE_IN_DURATION = 200;
export const FADE_OUT_DURATION = 100;

export interface AnimationState {
  type: AnimationType | null;
  priority: AnimationPriority | null;
  isProtected: boolean; // Protected animations cannot be interrupted
  startTime: number | null;
}

export type AnimationTransitionCallback = (from: AnimationType | null, to: AnimationType) => void;

export class AnimationStateManager {
  private currentState: AnimationState = {
    type: null,
    priority: null,
    isProtected: false,
    startTime: null
  };

  private transitionQueue: Array<{ type: AnimationType; priority: AnimationPriority; force?: boolean }> = [];
  private isProcessingTransition = false;
  private transitionCallbacks: Set<AnimationTransitionCallback> = new Set();

  /**
   * Register a callback for animation transitions
   */
  onTransition(callback: AnimationTransitionCallback): () => void {
    this.transitionCallbacks.add(callback);
    return () => {
      this.transitionCallbacks.delete(callback);
    };
  }

  /**
   * Check if an animation can start
   */
  canStart(type: AnimationType, force: boolean = false): boolean {
    const newPriority = getPriorityForType(type);

    // If forcing, always allow
    if (force) {
      return true;
    }

    // If current animation is protected, only allow higher priority
    if (this.currentState.isProtected) {
      return canInterrupt(this.currentState.priority, newPriority);
    }

    // Normal priority check
    return canInterrupt(this.currentState.priority, newPriority);
  }

  /**
   * Start an animation
   */
  startAnimation(type: AnimationType, force: boolean = false, isProtected: boolean = false): boolean {
    if (!this.canStart(type, force)) {
      console.log(`Cannot start ${type} animation - current: ${this.currentState.type} (priority ${this.currentState.priority})`);
      return false;
    }

    const priority = getPriorityForType(type);
    const previousType = this.currentState.type;

    // Update state
    this.currentState = {
      type,
      priority,
      isProtected: isProtected,
      startTime: Date.now()
    };

    // Notify transition callbacks
    this.transitionCallbacks.forEach(callback => {
      try {
        callback(previousType, type);
      } catch (error) {
        console.error('Error in transition callback:', error);
      }
    });

    console.log(`Animation started: ${type} (priority ${priority}, protected: ${isProtected})`);
    return true;
  }

  /**
   * Stop an animation
   */
  stopAnimation(type: AnimationType, force: boolean = false): boolean {
    // If forcing, always stop
    if (force) {
      const previousType = this.currentState.type;
      this.currentState = {
        type: null,
        priority: null,
        isProtected: false,
        startTime: null
      };

      if (previousType) {
        this.transitionCallbacks.forEach(callback => {
          try {
            callback(previousType, null as any);
          } catch (error) {
            console.error('Error in transition callback:', error);
          }
        });
      }

      console.log(`Animation force stopped: ${type}`);
      return true;
    }

    // Only stop if it's the current animation
    if (this.currentState.type === type) {
      const previousType = this.currentState.type;
      this.currentState = {
        type: null,
        priority: null,
        isProtected: false,
        startTime: null
      };

      this.transitionCallbacks.forEach(callback => {
        try {
          callback(previousType, null as any);
        } catch (error) {
          console.error('Error in transition callback:', error);
        }
      });

      console.log(`Animation stopped: ${type}`);
      return true;
    }

    return false;
  }

  /**
   * Get current animation state
   */
  getCurrentState(): Readonly<AnimationState> {
    return { ...this.currentState };
  }

  /**
   * Check if a specific animation type is currently active
   */
  isActive(type: AnimationType): boolean {
    return this.currentState.type === type;
  }

  /**
   * Check if any animation is currently protected
   */
  isProtected(): boolean {
    return this.currentState.isProtected;
  }

  /**
   * Set protection state for current animation
   */
  setProtection(isProtected: boolean): void {
    if (this.currentState.type) {
      this.currentState.isProtected = isProtected;
      console.log(`Animation protection ${isProtected ? 'enabled' : 'disabled'} for ${this.currentState.type}`);
    }
  }

  /**
   * Clear protection (used when animation completes naturally)
   */
  clearProtection(): void {
    if (this.currentState.isProtected) {
      this.currentState.isProtected = false;
      console.log(`Animation protection cleared for ${this.currentState.type}`);
    }
  }

  /**
   * Reset all state
   */
  reset(): void {
    const previousType = this.currentState.type;
    this.currentState = {
      type: null,
      priority: null,
      isProtected: false,
      startTime: null
    };

    if (previousType) {
      this.transitionCallbacks.forEach(callback => {
        try {
          callback(previousType, null as any);
        } catch (error) {
          console.error('Error in transition callback:', error);
        }
      });
    }

    this.transitionQueue = [];
    this.isProcessingTransition = false;
    console.log('Animation state manager reset');
  }

  /**
   * Check if speaking is currently active
   * When speaking is active, other animations should not use mouth morphs
   */
  isSpeakingActive(): boolean {
    return this.currentState.type === AnimationType.SPEAKING;
  }

  /**
   * Get animation weight for a given type
   * Used for blending animations
   */
  getWeightForType(type: AnimationType): number {
    return ANIMATION_WEIGHTS[type] ?? 0.3;
  }

  /**
   * Get fade durations
   */
  getFadeInDuration(): number {
    return FADE_IN_DURATION;
  }

  getFadeOutDuration(): number {
    return FADE_OUT_DURATION;
  }
}
